import { publicV2Api } from '@/lib/api-clients/rest-client';
import { useMarketStore } from '@/store/use-markets-store';
import { parseDecimalToBigInt } from '@/utils/value-format';
import { queryOptions, useQuery } from '@tanstack/react-query';

type OIsResponse = {
  ois: OIRecord[];
};

type OIRecord = {
  symbol: string;
  shortOi: string;
  longOi: string;
};

async function getOpenInterest(symbols?: string[]): Promise<OIRecord[]> {
  const res = await publicV2Api.get<OIsResponse>('/v2/oi', {
    params: symbols ? { symbols: symbols.join(',') } : {},
  });

  res.ois.forEach((ticker: OIRecord) => {
    const {
      setMarketData,
      marketData: { midPrice },
      marketSpec,
    } = useMarketStore(ticker.symbol).getState();
    const longCoinsSize = parseDecimalToBigInt(
      ticker.longOi,
      marketSpec.sizeDecimals,
    );
    const shortCoinsSize = parseDecimalToBigInt(
      ticker.shortOi,
      marketSpec.sizeDecimals,
    );
    setMarketData({
      openInterest: (longCoinsSize + shortCoinsSize) * midPrice,
    });
  });
  return res.ois;
}

const getOpenInterestQueryOptions = ({
  symbols,
  refetchInterval,
}: { symbols?: string[]; refetchInterval?: number | false } = {}) => {
  return queryOptions({
    queryKey: symbols ? ['open-interest', { symbols }] : ['open-interest'],
    queryFn: () => getOpenInterest(symbols),
    refetchInterval: refetchInterval ?? 10_000,
  });
};

type UseOpenInterestOptions = {
  symbols?: string[];
  disabled?: boolean;
  refetchInterval?: number | false;
};

export default function useOpenInterest({
  symbols,
  disabled,
  refetchInterval,
}: UseOpenInterestOptions) {
  return useQuery({
    ...getOpenInterestQueryOptions({ symbols, refetchInterval }),
    enabled: !disabled,
  });
}
